import React, { useState, Children, Fragment } from 'react';
import { OwnershipRecord, PendingChange, NonMemberSigner, ChildMember } from '../types/ownership';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Edit2, Trash2, ChevronRight, Building2, User } from 'lucide-react';
interface OwnershipTableProps {
  records: OwnershipRecord[];
  onEdit: (record: OwnershipRecord) => void;
  onDelete: (id: string) => void;
  isHistoricalView: boolean;
  pendingChanges?: PendingChange[];
  nonMemberSigners?: NonMemberSigner[];
  entityMemberChildren?: Record<string, ChildMember[]>;
}
export function OwnershipTable({
  records,
  onEdit,
  onDelete,
  isHistoricalView,
  pendingChanges = [],
  nonMemberSigners = [],
  entityMemberChildren = {}
}: OwnershipTableProps) {
  const today = new Date().toISOString().split('T')[0];
  const [expandedEntityIds, setExpandedEntityIds] = useState<Set<string>>(new Set());
  const toggleExpand = (recordId: string) => {
    setExpandedEntityIds(prev => {
      const next = new Set(prev);
      if (next.has(recordId)) next.delete(recordId);else next.add(recordId);
      return next;
    });
  };
  if (records.length === 0 && nonMemberSigners.length === 0) {
    return <div className="text-center py-12 bg-[#f3f2f2] rounded border border-dashed border-[#dddbda]" data-id="element-162">
        <p className="text-[#706e6b]" data-id="element-163">No ownership records found.</p>
      </div>;
  }
  return <div className="overflow-x-auto border border-[#dddbda] rounded-[4px]" data-id="element-164">
      <table className="w-full text-sm text-left" data-id="element-165">
        <thead className="bg-[#f3f2f2] text-[#3e3e3c] font-bold uppercase text-xs" data-id="element-166">
          <tr data-id="element-167">
            <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-168">Member</th>
            <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-169">Ownership %</th>
            <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-170">Start Date</th>
            <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-171">End Date</th>
            <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-172">Status</th>
            <th className="px-4 py-2 border-b border-[#dddbda] text-right" data-id="element-173">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-[#dddbda]" data-id="element-174">
          {records.map(record => {
          const pendingChange = pendingChanges.find(c => c.record.id === record.id);
          const isPendingAdd = pendingChange?.type === 'add';
          const isPendingEdit = pendingChange?.type === 'edit';
          const isPendingDelete = pendingChange?.type === 'delete';
          const isActive = record.effectiveStartDate <= today && (!record.effectiveEndDate || record.effectiveEndDate >= today);
          const isFuture = record.effectiveStartDate > today;
          const isHistorical = !isActive && !isFuture;
          const isEntityMember = record.memberRecordType === 'Entity';
          const children = isEntityMember ? entityMemberChildren[record.memberId] ?? [] : [];
          const isExpanded = expandedEntityIds.has(record.id);
          let rowClass = 'transition-colors ';
          if (isPendingAdd) rowClass += 'bg-green-50 border-l-4 border-l-green-500 ';else if (isPendingEdit) rowClass += 'bg-amber-50 border-l-4 border-l-amber-500 ';else if (isPendingDelete) rowClass += 'bg-red-50 border-l-4 border-l-red-500 opacity-60 ';else if (isHistorical) rowClass += 'bg-gray-50 text-gray-500 ';else rowClass += 'border-l-4 border-l-transparent ';
          if (isEntityMember && !isPendingDelete) rowClass += 'hover:bg-[#f3f2f2] cursor-pointer';else if (!isEntityMember) rowClass += 'hover:bg-[#f3f2f2]';
          return <Fragment key={record.id} data-id="element-175">
                <tr className={rowClass} onClick={isEntityMember && !isPendingDelete ? () => toggleExpand(record.id) : undefined} data-id="element-176">
                  <td className="px-4 py-3 font-medium text-[#080707]" data-id="element-177">
                    <div className={`flex items-center gap-2 ${isPendingDelete ? 'line-through' : ''}`} data-id="element-178">
                      {/* Expand chevron for entity members */}
                      {isEntityMember && !isPendingDelete ? <ChevronRight className={`w-4 h-4 text-[#706e6b] flex-shrink-0 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-179" /> : <span className="w-4 flex-shrink-0" data-id="element-180" />}
                      {/* Entity vs Individual icon */}
                      {isEntityMember ? <Building2 className="w-3.5 h-3.5 text-[#0176d3] flex-shrink-0" data-id="element-181" /> : <User className="w-3.5 h-3.5 text-[#706e6b] flex-shrink-0" data-id="element-182" />}
                      <span data-id="element-183">{record.memberName}</span>
                      {record.isPrimaryMember && <span className="inline-flex" data-id="element-184">
                          <Badge variant="brand" data-id="element-185">Primary</Badge>
                        </span>}
                      {isEntityMember && (record.memberEin ? <span className="font-mono text-xs text-[#706e6b] bg-[#f3f2f2] border border-[#dddbda] px-1.5 py-0.5 rounded" data-id="element-186">
                            {record.memberEin}
                          </span> : <Badge variant="neutral" data-id="element-187">Entity</Badge>)}
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-188">
                    <span className={isPendingDelete ? 'line-through' : ''} data-id="element-189">
                      {record.ownershipPercentage}%
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-190">
                    <span className={isPendingDelete ? 'line-through' : ''} data-id="element-191">
                      {new Date(record.effectiveStartDate).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-192">
                    <span className={isPendingDelete ? 'line-through' : ''} data-id="element-193">
                      {record.effectiveEndDate ? new Date(record.effectiveEndDate).toLocaleDateString() : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-194">
                    {isPendingAdd ? <Badge className="bg-green-100 text-green-800 border border-green-200" data-id="element-195">
                        New
                      </Badge> : isPendingEdit ? <Badge className="bg-amber-100 text-amber-800 border border-amber-200" data-id="element-196">
                        Modified
                      </Badge> : isPendingDelete ? <Badge className="bg-red-100 text-red-800 border border-red-200" data-id="element-197">
                        Pending Delete
                      </Badge> : isFuture ? <Badge variant="warning" data-id="element-198">Future</Badge> : isActive ? <Badge variant="success" data-id="element-199">Active</Badge> : <Badge variant="neutral" data-id="element-200">Historical</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()} data-id="element-201">
                    <div className="flex items-center justify-end gap-2" data-id="element-202">
                      {isPendingDelete ? <button className="p-1 text-gray-400 cursor-not-allowed" disabled title="Pending Deletion" data-id="element-203">
                          <Trash2 className="w-4 h-4" data-id="element-204" />
                        </button> : <>
                          <button onClick={() => onEdit(record)} className="p-1 text-[#0176d3] hover:bg-[#eef4ff] rounded" title="Edit" data-id="element-205">
                            <Edit2 className="w-4 h-4" data-id="element-206" />
                          </button>
                          {isFuture || isPendingAdd ? <button onClick={() => onDelete(record.id)} className="p-1 text-[#ba0517] hover:bg-[#fff1f0] rounded" title="Delete" data-id="element-207">
                              <Trash2 className="w-4 h-4" data-id="element-208" />
                            </button> : <button className="p-1 text-gray-300 cursor-not-allowed" title="Cannot delete active/historical records" disabled data-id="element-209">
                              <Trash2 className="w-4 h-4" data-id="element-210" />
                            </button>}
                        </>}
                    </div>
                  </td>
                </tr>

                {/* Expanded child members sub-table */}
                {isEntityMember && isExpanded && children.length > 0 && <tr data-id="element-211">
                    <td colSpan={6} className="p-0 bg-[#f3f2f2] border-l-4 border-l-[#0176d3]" data-id="element-212">
                      <div className="px-6 py-3" data-id="element-213">
                        <div className="flex items-center gap-2 mb-2" data-id="element-214">
                          <Building2 className="w-3.5 h-3.5 text-[#0176d3]" data-id="element-215" />
                          <span className="text-xs font-bold uppercase tracking-wide text-[#0176d3]" data-id="element-216">
                            {record.memberName} — Members
                          </span>
                          <span className="text-xs text-[#706e6b]" data-id="element-217">
                            ({children.length})
                          </span>
                        </div>
                        <div className="border border-[#dddbda] rounded-[4px] overflow-hidden" data-id="element-218">
                          <table className="w-full text-sm text-left" data-id="element-219">
                            <thead className="bg-[#eaf2fb] text-[#3e3e3c] font-bold uppercase text-xs" data-id="element-220">
                              <tr data-id="element-221">
                                <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-222">
                                  Member Name
                                </th>
                                <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-223">
                                  Ownership %
                                </th>
                                <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-224">
                                  EIN
                                </th>
                                <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-225">
                                  Type
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-[#dddbda]" data-id="element-226">
                              {children.map(child => <tr key={child.id} className="border-l-4 border-l-transparent" data-id="element-227">
                                  <td className="px-4 py-2.5 font-medium text-[#080707]" data-id="element-228">
                                    <div className="flex items-center gap-2" data-id="element-229">
                                      {child.recordType === 'Entity' ? <Building2 className="w-3.5 h-3.5 text-[#0176d3] flex-shrink-0" data-id="element-230" /> : <User className="w-3.5 h-3.5 text-[#706e6b] flex-shrink-0" data-id="element-231" />}
                                      <div data-id="element-232">
                                        <div data-id="element-233">{child.name}</div>
                                        {child.title && <div className="text-xs text-[#706e6b]" data-id="element-234">
                                            {child.title}
                                          </div>}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2.5 text-[#080707]" data-id="element-235">
                                    {child.ownershipPercentage}%
                                  </td>
                                  <td className="px-4 py-2.5 font-mono text-[#3e3e3c] text-xs" data-id="element-236">
                                    {child.ein}
                                  </td>
                                  <td className="px-4 py-2.5" data-id="element-237">
                                    <Badge variant={child.recordType === 'Entity' ? 'brand' : 'neutral'} data-id="element-238">
                                      {child.recordType}
                                    </Badge>
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>}
              </Fragment>;
        })}

          {/* Non-Member Signers Section */}
          {nonMemberSigners.length > 0 && <>
              <tr data-id="element-239">
                <td colSpan={6} className="px-4 py-2 bg-[#f3f2f2] border-t-2 border-[#dddbda]" data-id="element-240">
                  <span className="text-xs font-bold uppercase tracking-wide text-[#706e6b]" data-id="element-241">
                    Non-Member Signers
                  </span>
                  <span className="text-xs text-[#706e6b] ml-2 font-normal normal-case tracking-normal" data-id="element-242">
                    — Guarantors on entity deals who are not part of the
                    ownership structure
                  </span>
                </td>
              </tr>
              {nonMemberSigners.map(signer => <tr key={signer.id} className="border-l-4 border-l-[#ecebea] bg-[#fafaf9]" data-id="element-243">
                  <td className="px-4 py-3 font-medium text-[#080707]" data-id="element-244">
                    <div data-id="element-245">{signer.name}</div>
                    {signer.email && <div className="text-xs text-[#706e6b] mt-0.5" data-id="element-246">
                        {signer.email}
                      </div>}
                  </td>
                  <td className="px-4 py-3 text-[#706e6b]" data-id="element-247">
                    <span className="text-xs" data-id="element-248">—</span>
                  </td>
                  <td className="px-4 py-3 text-[#3e3e3c]" colSpan={2} data-id="element-249">
                    <div className="relative inline-block group" data-id="element-250">
                      <button className="text-[#0176d3] font-medium text-sm hover:underline cursor-pointer" type="button" data-id="element-251">
                        {signer.loanNumbers.length}{' '}
                        {signer.loanNumbers.length === 1 ? 'loan' : 'loans'}
                      </button>
                      <div className="absolute left-0 bottom-full mb-1.5 hidden group-hover:block z-40" data-id="element-252">
                        <div className="bg-[#080707] text-white text-xs rounded-[4px] shadow-lg py-2 px-3 whitespace-nowrap" data-id="element-253">
                          <div className="font-bold mb-1 text-[#b0adab]" data-id="element-254">
                            Associated Loans
                          </div>
                          {signer.loanNumbers.map(ln => <div key={ln} className="py-0.5" data-id="element-255">
                              {ln}
                            </div>)}
                          <div className="absolute left-4 top-full w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-[#080707]" data-id="element-256" />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-257">
                    <Badge variant="neutral" data-id="element-258">Guarantor</Badge>
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-259">
                    <span className="text-xs text-[#706e6b]" data-id="element-260">Read-only</span>
                  </td>
                </tr>)}
            </>}
        </tbody>
      </table>
    </div>;
}